import { createContext, useContext, useState, useEffect } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [brand, setBrand] = useState(() => {
    try { return JSON.parse(localStorage.getItem("brand") || "null"); } catch { return null; }
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const fetchBrand = async () => {
    try {
      const { data } = await api.get("/my-brand");
      const b = data?.brand || null;
      setBrand(b);
      if (b) localStorage.setItem("brand", JSON.stringify(b));
      else localStorage.removeItem("brand");
      if (b?.name) document.title = b.name;
    } catch { /* brand is cosmetic */ }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setIsAuthenticated(true);
      fetchBrand();
    } catch (error) {
      // Token invalid or expired
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.access_token);
    setUser(data.user);
    setIsAuthenticated(true);
    fetchBrand();
    return data;
  };

  const loginWithToken = (token, userData) => {
    localStorage.setItem("token", token);
    setUser(userData);
    setIsAuthenticated(true);
    fetchBrand();
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("token", data.access_token);
    setUser(data.user);
    setIsAuthenticated(true);
    return data;
  };

  const registerPaid = async (email, password, name) => {
    const { data } = await api.post("/auth/register-paid", { email, password, name });
    localStorage.setItem("token", data.access_token);
    setUser(data.user);
    setIsAuthenticated(true);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("brand");
    document.title = "LeadBot";
    setBrand(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = async (updates) => {
    const { data } = await api.put("/auth/me", updates);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        brand,
        refreshBrand: fetchBrand,
        loading,
        isAuthenticated,
        login,
        loginWithToken,
        register,
        registerPaid,
        logout,
        updateUser,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
